import React, { useCallback, useEffect, useState } from "react";
import {
  Button,
  Card,
  Container,
  Dimmer,
  Loader,
  Message,
} from "semantic-ui-react";
import styled from "styled-components";
import { elevation, palette } from "@conserveit/blocks";
import { Trans } from "@lingui/macro";
import { ModalState } from "./index";
import SaveModal from "./SaveModal";
import DiscardModal from "./DiscardModal";
import EditableCardField from "./EditableCardField";
import {
  getSiteDetails,
  postSiteDetails,
  SiteDetails,
} from "../../../api/siteDetailsApi";
import SiteDropdown from "./components/SiteDropdown";

const CardContainer = styled(Container)`
  display: flex;
  justify-content: center;
  width: 750px !important;
`;

const StyledMessage = styled(Message)`
  width: 750px !important;
  margin: 0;
`;

const CardShadow = styled.div`
  border-radius: 5px;
  box-shadow: ${elevation.content};
`;

const CardGroup = styled(Card.Group)`
  display: flex;
  margin: 0 !important;
`;

const StyledCard = styled(Card)`
  width: 750px !important;
  margin: 0 !important;
`;

const HeaderCardContainer = styled.div`
  font-size: 20px;
  font-weight: 700;
  display: flex;
  height: 70px;
  background-color: #f9fafb;
  vertical-align: middle !important;
`;

const StyledHeaderContent = styled(Card.Content)`
  display: flex;
  justify-content: flex-start;
  align-items: center;
  margin-left: 50px;
  height: 70px;
  vertical-align: middle;
`;

const StyledCardHeader = styled(Card.Header)`
  vertical-align: middle;
`;

const StyledRowContainer = styled.div`
  display: flex;
  justify-content: center;
  width: 700px;
`;

const StyledCardRow = styled.div`
  display: flex;
  width: 100%;
`;

const StyledLeftColumn = styled.div`
  display: flex;
  margin-left: 15px;
  width: 100%;
  justify-content: center !important;
`;

const StyledRightColumn = styled.div`
  display: flex;
  margin-left: 15px;
  width: 100%;
  justify-content: center !important;
`;

const StyledColumn = styled.div`
  display: flex;
  flex-direction: column;
  margin: 5px 15px 10px 15px;
`;

const InputColumn = styled.div`
  display: flex;
  flex-direction: column;
  margin: 6px 5px 0 5px;
  width: 150px;
`;

const StyledDescription = styled(Card.Description)`
  margin: 15px 0 15px 0;
  height: 60px;
`;

const CardButtonContainer = styled.div`
  display: flex;
  justify-content: flex-end;
  margin-top: 30px;
`;

const ButtonMargin = styled.div`
  margin: 5px 35px 5px 35px;
`;

const EditButton = styled(Button)`
  width: 100px;
  padding-left: 0 !important;
  padding-right: 0 !important;
  color: ${palette.pureWhite} !important;
  background-color: ${palette.edit} !important;
`;

const DiscardButton = styled(Button)`
  width: 100px;
  padding-left: 0 !important;
  padding-right: 0 !important;
  color: ${palette.pureWhite} !important;
  background-color: ${palette.danger} !important;
`;

const SaveButton = styled(Button)`
  width: 100px;
  padding-left: 0 !important;
  padding-right: 0 !important;
  color: ${palette.pureWhite} !important;
  background-color: ${palette.success} !important;
`;

const defaultCard = {} as SiteDetails;

interface Props {
  sites: Insite.AppState["userSites"]["sites"];
  setCurrentSite: (syncoId: string) => void;
  currentSite: string;
}

const APICard: React.FC<Props> = ({ sites, setCurrentSite, currentSite }) => {
  const [cardDescription, setCardDescription] =
    useState<SiteDetails>(defaultCard);
  const [errorHandlingMessage, setErrorHandlingMessage] = useState<String>("");
  const [errorHandlingEndPoint, setErrorHandlingEndPoint] = useState<String>("");

  // Using blank space naming convention for unused destructured variables
  // We will use 'error' for toaster notification and 'loading' for loading animation in the future
  const [loading, setLoading] = useState<boolean>(true);
  const [errorState, setError] = useState<boolean>(false);

  const [prevCardDescription, setPrevCardDescription] =
    useState<SiteDetails>(cardDescription);

  const [isCardEditable, setIsCardEditable] = useState<boolean>(false);
  const [modalState, setModalState] = useState<ModalState>("HIDE");

  const getData = useCallback(() => {
    setLoading(true);
    getSiteDetails(
      currentSite,
      (data) => {
        setPrevCardDescription(data);
        setCardDescription(data);
        setLoading(false);
        setError(false);
      },
      (errorResponse) => {
        setErrorHandlingMessage(errorResponse.data);
        setErrorHandlingEndPoint(errorResponse.url);
        setError(true);
        setLoading(false);
      }
    );
  }, [
    getSiteDetails,
    currentSite,
    setPrevCardDescription,
    setCardDescription,
    setErrorHandlingMessage,
    setLoading,
    setError,
  ]);

  useEffect(() => {
    getData();
  }, [getData]);

  const onModalDiscard = () => {
    setIsCardEditable(false);
    setModalState("HIDE");
    setCardDescription(prevCardDescription);
  };

  const onModalConfirm = () => {
    setIsCardEditable(false);
    setModalState("HIDE");
    postSiteDetails(
      currentSite,
      cardDescription,
      (data) => {
        setPrevCardDescription(data);
        setLoading(false);
        setError(false);
      },
      (errorResponse) => {
        setErrorHandlingMessage(errorResponse.data);
        setErrorHandlingEndPoint(errorResponse.url);
        setLoading(false);
        setError(true);
        getData();
      }
    );
  };

  const onModalCancel = () => {
    setModalState("HIDE");
  };

  const cardHeader = sites[currentSite]?.label || "Unknown";

  return (
    <>
      <CardContainer>
        <SiteDropdown
          sites={sites}
          setCurrentSite={setCurrentSite}
          currentSite={currentSite}
          disabled={isCardEditable}
        />
        <CardGroup>
          <CardShadow>
            <Dimmer.Dimmable dimmed={loading}>
              <Dimmer active={loading} inverted>
                <Loader>Loading</Loader>
              </Dimmer>
              {!errorState && (
                <StyledCard fluid>
                  <HeaderCardContainer>
                    <StyledHeaderContent>
                      <StyledCardHeader>{cardHeader}</StyledCardHeader>
                    </StyledHeaderContent>
                    <StyledHeaderContent></StyledHeaderContent>
                  </HeaderCardContainer>
                  <Card.Content>
                    <>
                      <StyledRowContainer>
                        <StyledCardRow>
                          <StyledLeftColumn>
                            <StyledColumn>
                              <StyledDescription>
                                <strong>
                                  <Trans id="site_details.card.ref_number">
                                    Customer Ref. Number:
                                  </Trans>
                                </strong>
                              </StyledDescription>
                              <StyledDescription>
                                <strong>
                                  <Trans id="site_details.card.customer_number">
                                    Customer Number:
                                  </Trans>
                                </strong>
                              </StyledDescription>
                              <StyledDescription>
                                <strong>
                                  <Trans id="site_details.card.address_code">
                                    Address Code:
                                  </Trans>
                                </strong>
                              </StyledDescription>
                            </StyledColumn>
                            <InputColumn>
                              <EditableCardField
                                data-testid="card__custName"
                                content={cardDescription.custName}
                                isCardEditable={isCardEditable}
                                onChange={(content: string) =>
                                  setCardDescription({
                                    ...cardDescription,
                                    custName: content,
                                  })
                                }
                              />

                              <EditableCardField
                                data-testid="card__custNmbr"
                                content={cardDescription.custNmbr}
                                isCardEditable={isCardEditable}
                                onChange={(content: string) =>
                                  setCardDescription({
                                    ...cardDescription,
                                    custNmbr: content,
                                  })
                                }
                              />

                              <EditableCardField
                                data-testid="card__adrsCode"
                                content={cardDescription.adrsCode}
                                isCardEditable={isCardEditable}
                                onChange={(content: string) =>
                                  setCardDescription({
                                    ...cardDescription,
                                    adrsCode: content,
                                  })
                                }
                              />
                            </InputColumn>
                          </StyledLeftColumn>
                          <StyledRightColumn>
                            <StyledColumn>
                              <StyledDescription>
                                <strong>
                                  <Trans id="site_details.card.contract_number">
                                    Contract Number:
                                  </Trans>
                                </strong>
                              </StyledDescription>
                              <StyledDescription>
                                <strong>
                                  <Trans id="site_details.card.equipment_type">
                                    Equipment Type:
                                  </Trans>
                                </strong>
                              </StyledDescription>
                              <StyledDescription>
                                <strong>
                                  <Trans id="site_details.card.site_id_number">
                                    Site ID:
                                  </Trans>
                                </strong>
                              </StyledDescription>
                              <StyledDescription>
                                <strong>
                                  <Trans id="site_details.card.site_details_id_number">
                                    Site Details ID:
                                  </Trans>
                                </strong>
                              </StyledDescription>
                            </StyledColumn>
                            <InputColumn>
                              <EditableCardField
                                data-testid="card__contractNumber"
                                content={cardDescription.contractNumber}
                                isCardEditable={isCardEditable}
                                onChange={(content: string) =>
                                  setCardDescription({
                                    ...cardDescription,
                                    contractNumber: content,
                                  })
                                }
                              />
                              <EditableCardField
                                data-testid="card__equipmentId"
                                content={cardDescription.equipmentId}
                                isCardEditable={isCardEditable}
                                onChange={(content: string) =>
                                  setCardDescription({
                                    ...cardDescription,
                                    equipmentId: content,
                                  })
                                }
                              />
                              <EditableCardField
                                data-testid="card__siteId"
                                content={cardDescription.siteFk}
                                isCardEditable={false}
                                onChange={() => {}}
                              />
                              <EditableCardField
                                data-testid="card__id"
                                content={cardDescription.iwsSiteDetailsPk}
                                isCardEditable={false}
                                onChange={() => {}}
                              />
                            </InputColumn>
                          </StyledRightColumn>
                        </StyledCardRow>
                      </StyledRowContainer>
                      <CardButtonContainer>
                        {!isCardEditable && (
                          <ButtonMargin>
                            <EditButton
                              data-testid="card-edit-button"
                              onClick={() => setIsCardEditable(true)}
                            >
                              <Trans id="site_details.edit">Edit</Trans>
                            </EditButton>
                          </ButtonMargin>
                        )}
                        {isCardEditable && (
                          <>
                            <ButtonMargin>
                              <DiscardButton
                                data-testid="card-discard-button"
                                onClick={() => setModalState("CONFIRM_DISCARD")}
                              >
                                <Trans id="site_details.discard">Discard</Trans>
                              </DiscardButton>
                            </ButtonMargin>
                            <ButtonMargin>
                              <SaveButton
                                data-testid="card-save-button"
                                onClick={() => setModalState("CONFIRM_SAVE")}
                              >
                                <Trans id="site_details.save">Save</Trans>
                              </SaveButton>
                            </ButtonMargin>
                          </>
                        )}
                      </CardButtonContainer>
                    </>
                  </Card.Content>
                </StyledCard>
              )}
              {errorState && (
                <StyledMessage>
                  <Message.Header>Error</Message.Header>
                    { errorHandlingEndPoint !== "undefined" && (
                      <p> Request unsuccessful for this endpoint:  {errorHandlingEndPoint} </p>
                    )}                   
                    <p data-testid="api_error_handling_response">{errorHandlingMessage}</p>
                    <p>If this error persists, please contact support with the error message and steps to replicate via the support portal or email support@conserveitiot.com.</p>
                </StyledMessage>
              )}
            </Dimmer.Dimmable>
          </CardShadow>
        </CardGroup>
      </CardContainer>
      {modalState === "CONFIRM_SAVE" && (
        <SaveModal
          data-testid="card__confirm-save-modal"
          onModalConfirm={onModalConfirm}
          onModalCancel={onModalCancel}
        />
      )}
      {modalState === "CONFIRM_DISCARD" && (
        <DiscardModal
          data-testid="card__confirm-discard-modal"
          onModalConfirm={onModalDiscard}
          onModalCancel={onModalCancel}
        />
      )}
    </>
  );
};

export default APICard;
